import { useState, type JSX } from 'react';
import { toast } from 'sonner';
import { Check, ExternalLink, EyeOff, MessageSquare, Send, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { RowActionItem } from '@/components/ui/row-actions';
import { STEPS, WHY_MARK_MESSAGED } from '@/lib/outreach-copy';
import {
  enrichmentField,
  leadStep,
  messagedAgo,
  messagedNames,
  queueActionError,
  useMarkContacted,
  useMarkIrrelevant,
  useUnmarkContacted,
  useUnmarkIrrelevant,
  whoMessaged,
  type MyMessagedRecord,
  type QueueTab,
} from '@/hooks/useDiscoveryQueue';
import type { DiscoveryProspect } from '@/api/types';
import { LeadRow } from './LeadRow';
import { SkipDialog } from './dialogs';
import { compactNumber, prospectProfileUrl } from './profile';

/**
 * A queue lead on one of the three tabs. One primary button per tab, the
 * rest in the kebab:
 *
 *   To message          I messaged them   · Log reply, Skip, Open profile
 *   Messaged · waiting  Log reply         · Undo messaged, Skip, Open profile
 *   Done (skipped)      Unskip            · Log reply, Open profile
 *   Done (logged)       —                 · Open profile
 *
 * Every action keeps its old behaviour and invalidations; only where it sits
 * changed. Messaging is not a claim, so nothing here disables because a
 * teammate messaged too.
 */
export function ProspectLeadRow({
  prospect,
  tab,
  myInviterId,
  myTeam,
  messaged,
  showFolder,
  onLogReply,
}: {
  prospect: DiscoveryProspect;
  tab: QueueTab;
  myInviterId: number | null;
  myTeam: string | null;
  /** The caller's own messaged record for this creator, when known. */
  messaged?: MyMessagedRecord;
  showFolder: boolean;
  onLogReply: (p: DiscoveryProspect) => void;
}): JSX.Element {
  const handle = `@${prospect.targetIdentifier}`;
  const href = prospectProfileUrl(prospect);

  const markMessaged = useMarkContacted();
  const unmarkMessaged = useUnmarkContacted();
  const markSkipped = useMarkIrrelevant();
  const unmarkSkipped = useUnmarkIrrelevant();
  const [skipOpen, setSkipOpen] = useState(false);
  const onError = (e: unknown): void => {
    toast.error(queueActionError(e));
  };

  const logged = prospect.status === 'PROMOTED';
  const iSkipped = prospect.flaggedByMe === true;
  const iMessaged = prospect.contactedByMe === true;
  const messagedPending = markMessaged.isPending || unmarkMessaged.isPending;
  const skipPending = markSkipped.isPending || unmarkSkipped.isPending;

  const assignedToMe = myInviterId != null && prospect.assignedToInviterId === myInviterId;
  const assignedToTeam = !assignedToMe && myTeam != null && prospect.assignedToTeam === myTeam;

  const names = messagedNames(prospect.contactedBy ?? [], myInviterId).names;
  const when = iMessaged ? messagedAgo(messaged?.lastContactedAt) : null;

  const markAsMessaged = (): void =>
    markMessaged.mutate(
      { publicId: prospect.publicId },
      { onSuccess: () => toast.success(`Marked ${handle} as messaged`), onError },
    );
  const undoMessaged = (): void =>
    unmarkMessaged.mutate(prospect.publicId, {
      onSuccess: () => toast.success('Removed your messaged mark'),
      onError,
    });
  const unskip = (): void =>
    unmarkSkipped.mutate(prospect.publicId, {
      onSuccess: () => toast.success(`Unskipped ${handle}. It's back in your list.`),
      onError,
    });

  const logReplyItem: RowActionItem = {
    label: STEPS.logged.verb,
    icon: MessageSquare,
    onClick: () => onLogReply(prospect),
  };
  const skipItem: RowActionItem = {
    label: 'Skip (wrong fit)',
    icon: EyeOff,
    disabled: skipPending,
    onClick: () => setSkipOpen(true),
  };
  const undoItem: RowActionItem = {
    label: `${STEPS.messaged.undo} messaged`,
    icon: Undo2,
    disabled: messagedPending,
    onClick: undoMessaged,
  };
  const profileItem: RowActionItem | null = href
    ? {
        label: 'Open profile',
        icon: ExternalLink,
        onClick: () => window.open(href, '_blank', 'noopener,noreferrer'),
      }
    : null;

  let primary: JSX.Element | null = null;
  let actions: Array<RowActionItem | null> = [];
  if (tab === 'message') {
    primary = (
      <Button size="sm" disabled={messagedPending} onClick={markAsMessaged} title={WHY_MARK_MESSAGED}>
        <Send className="mr-1 h-3.5 w-3.5" />
        {markMessaged.isPending ? 'Saving…' : STEPS.messaged.verb}
      </Button>
    );
    actions = [logReplyItem, skipItem, profileItem];
  } else if (tab === 'waiting') {
    primary = (
      <Button size="sm" onClick={() => onLogReply(prospect)} title={STEPS.logged.means}>
        <MessageSquare className="mr-1 h-3.5 w-3.5" />
        {STEPS.logged.verb}
      </Button>
    );
    actions = [undoItem, skipItem, profileItem];
  } else if (logged) {
    primary = (
      <span className="inline-flex h-8 items-center gap-1 px-2 text-sm text-muted-foreground">
        <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
        Reply logged
      </span>
    );
    actions = [profileItem];
  } else {
    primary = (
      <Button
        size="sm"
        variant="outline"
        disabled={skipPending}
        onClick={unskip}
        title="Put this creator back in your list"
      >
        <Undo2 className="mr-1 h-3.5 w-3.5" />
        {unmarkSkipped.isPending ? 'Undoing…' : 'Unskip'}
      </Button>
    );
    actions = [logReplyItem, iMessaged ? undoItem : null, profileItem];
  }

  return (
    <>
      <LeadRow
        channel={prospect.channel}
        handle={prospect.targetIdentifier}
        href={href}
        followers={compactNumber(enrichmentField(prospect, 'followers'))}
        avgViews={compactNumber(enrichmentField(prospect, 'avgViews'))}
        highlight={assignedToMe ? 'me' : assignedToTeam ? 'team' : null}
        badge={
          assignedToMe ? (
            <span className="inline-flex shrink-0 items-center rounded-full border border-brand/30 bg-brand/10 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-brand">
              Assigned to you
            </span>
          ) : null
        }
        meta={[
          showFolder && (prospect.sourceFolderName ?? 'Unfiled'),
          whoMessaged(names),
          when,
          iSkipped &&
            !logged &&
            (prospect.flagNote ? `You skipped: ${prospect.flagNote}` : 'You skipped this one. Teammates still see it.'),
        ]}
        step={tab === 'done' && iSkipped && !logged ? 'skipped' : leadStep(prospect)}
        primary={primary}
        actions={actions}
      />
      {skipOpen && (
        <SkipDialog
          prospect={prospect}
          onClose={() => setSkipOpen(false)}
          onSubmit={(note) =>
            markSkipped.mutate(
              { publicId: prospect.publicId, note },
              {
                onSuccess: () => {
                  setSkipOpen(false);
                  toast.success('Skipped. Only you stop seeing it.');
                },
                onError,
              },
            )
          }
          pending={markSkipped.isPending}
        />
      )}
    </>
  );
}
